import { Injectable, inject } from '@angular/core';
import { filter } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RoleShared } from '@portal/models/roleShared';
import { UserInfo } from '@portal/models/userInfo';
import { BuilderInfoService } from '@portal/builder';
import { AuthService } from '../auth.service';
import { PermissionsFullAccess, PermissionsMap, PermissionsRead, PermissionsUpdate } from './permissions.interface';
import PrivilegesEnum = RoleShared.PrivilegesEnum;
import ProfileTypeEnum = UserInfo.ProfileTypeEnum;

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private readonly auth = inject(AuthService);
  private readonly builderInfoService = inject(BuilderInfoService);

  protected permissionsMap: PermissionsMap;
  protected profileType: ProfileTypeEnum;

  constructor() {
    this.auth
      .getUserInfo()
      .pipe(
        filter(info => !!info),
        untilDestroyed(this),
      )
      .subscribe(i => {
        if (!i.privileges) {
          return;
        }
        this.permissionsMap = this.getPermissionsMap(i.privileges);
        this.profileType = i.profileType;
      });
  }

  get profilePermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.MERCHANT_PROFILE_READ),
      edit: this.hasPermission(PrivilegesEnum.MERCHANT_COMPANY_WRITE),
    };
  }

  // TODO add real permissions check
  get dashboardPermissions(): PermissionsUpdate {
    return {
      view: true,
      edit: true,
    };
  }

  get companiesPermissions(): PermissionsFullAccess {
    return {
      view: this.hasPermission(PrivilegesEnum.COMPANIES_READ),
      create: this.hasPermission(PrivilegesEnum.COMPANIES_WRITE),
      edit: this.hasPermission(PrivilegesEnum.COMPANIES_WRITE),
      delete: this.hasPermission(PrivilegesEnum.COMPANIES_WRITE),
    };
  }

  get merchantCompanyPermissions(): PermissionsFullAccess {
    return {
      view: this.hasPermission(PrivilegesEnum.MERCHANT_COMPANY_WRITE),
      create: this.hasPermission(PrivilegesEnum.MERCHANT_COMPANY_WRITE),
      edit: this.hasPermission(PrivilegesEnum.MERCHANT_COMPANY_WRITE),
      delete: this.hasPermission(PrivilegesEnum.MERCHANT_COMPANY_WRITE),
    };
  }

  get membersPermissions(): PermissionsFullAccess {
    return {
      view: this.hasPermission(PrivilegesEnum.MEMBERS_READ),
      create: this.hasPermission(PrivilegesEnum.MEMBERS_WRITE),
      delete: this.hasPermission(PrivilegesEnum.MEMBERS_WRITE),
      edit: this.hasPermission(PrivilegesEnum.MEMBERS_WRITE),
    };
  }

  get usersPermissions(): PermissionsFullAccess {
    return {
      view: this.hasPermission(PrivilegesEnum.CLIENTS_READ),
      edit: this.hasPermission(PrivilegesEnum.CLIENTS_WRITE),
      create: this.hasPermission(PrivilegesEnum.CLIENTS_WRITE),
      delete: this.hasPermission(PrivilegesEnum.CLIENTS_WRITE),
    };
  }

  get productPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.ITEMS_READ),
      edit: this.hasPermission(PrivilegesEnum.ITEMS_WRITE),
    };
  }

  get orderPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.ORDER_READ),
      edit: this.hasPermission(PrivilegesEnum.ORDER_WRITE),
    };
  }

  get bnplPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.BNPL_READ),
      edit: this.hasPermission(PrivilegesEnum.BNPL_WRITE),
    };
  }

  get futureSkusPermissions(): PermissionsUpdate {
    const available = !this.builderInfoService?.settings?.disableFutureProducts;
    return {
      view: available,
      edit: available,
    };
  }

  get deliveryOrdersPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.DELIVERY_ORDERS_READ),
      edit: this.hasPermission(PrivilegesEnum.DELIVERY_ORDERS_WRITE),
    };
  }

  get orderSumPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.ORDER_SUM_WRITE),
      edit: this.hasPermission(PrivilegesEnum.ORDER_SUM_WRITE),
    };
  }

  get returnsPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.ORDER_RETURNS_READ),
      edit: this.hasPermission(PrivilegesEnum.ORDER_RETURNS_WRITE),
    };
  }

  // TODO: add actual permissions
  get requestsForQuotations(): PermissionsUpdate {
    return {
      view: true,
      edit: true,
    };
  }

  get requestsPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.REQUESTS_READ),
      edit: this.hasPermission(PrivilegesEnum.REQUESTS_WRITE),
    };
  }

  get reportsPermissions(): PermissionsRead {
    return {
      view: this.hasPermission(PrivilegesEnum.LOGS_REPORT_READ),
    };
  }

  get pagesPermissions(): PermissionsFullAccess {
    return {
      view: this.hasPermission(PrivilegesEnum.PAGES_READ),
      edit: this.hasPermission(PrivilegesEnum.PAGES_WRITE),
      delete: this.hasPermission(PrivilegesEnum.PAGES_WRITE),
      create: this.hasPermission(PrivilegesEnum.PAGES_WRITE),
    };
  }

  get financialProductsPermissions(): PermissionsFullAccess {
    return {
      view: this.hasPermission(PrivilegesEnum.FIN_PRODUCTS_READ),
      edit: this.hasPermission(PrivilegesEnum.FIN_PRODUCTS_WRITE),
      delete: this.hasPermission(PrivilegesEnum.FIN_PRODUCTS_WRITE),
      create: this.hasPermission(PrivilegesEnum.FIN_PRODUCTS_WRITE),
    };
  }

  get notificationsPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.NOTIFICATIONS_READ),
      edit: this.hasPermission(PrivilegesEnum.NOTIFICATIONS_WRITE),
    };
  }

  get userNotificationsPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.TARGET_NOTIFICATIONS_READ),
      edit: this.hasPermission(PrivilegesEnum.TARGET_NOTIFICATIONS_WRITE),
    };
  }

  get groupsPermissions(): PermissionsUpdate {
    return {
      view: true,
      edit: true,
    };
  }

  get templatesPermissions(): PermissionsFullAccess {
    return {
      view: this.hasPermission(PrivilegesEnum.TEMPLATES_READ),
      edit: this.hasPermission(PrivilegesEnum.TEMPLATE_WRITE),
      delete: this.hasPermission(PrivilegesEnum.TEMPLATE_WRITE),
      create: this.hasPermission(PrivilegesEnum.TEMPLATE_WRITE),
    };
  }

  get articlesPermissions(): PermissionsFullAccess {
    return {
      view: this.hasPermission(PrivilegesEnum.ARTICLES_READ),
      edit: this.hasPermission(PrivilegesEnum.ARTICLES_WRITE),
      delete: this.hasPermission(PrivilegesEnum.ARTICLES_WRITE),
      create: this.hasPermission(PrivilegesEnum.ARTICLES_WRITE),
    };
  }

  get auditPermissions(): PermissionsRead {
    return {
      view:
        this.hasPermission(PrivilegesEnum.AUDIT_OPERATORS_READ) || this.hasPermission(PrivilegesEnum.AUDIT_USERS_READ),
    };
  }

  get auditActionsPermissions() {
    return {
      operators: this.hasPermission(PrivilegesEnum.AUDIT_OPERATORS_READ),
      users: this.hasPermission(PrivilegesEnum.AUDIT_USERS_READ),
    };
  }

  get userProfilePermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.USER_PROFILE_READ),
      edit: this.hasPermission(PrivilegesEnum.USER_PROFILE_WRITE),
    };
  }

  get pageBuilderPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.BLOCKS_READ),
      edit: this.hasPermission(PrivilegesEnum.BLOCKS_WRITE),
    };
  }

  get seoTagsPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.PAGE_SEO_TAGS_READ),
      edit: this.hasPermission(PrivilegesEnum.PAGE_SEO_TAGS_WRITE),
    };
  }

  get settingsPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.MARKETPLACE_SETTINGS_READ),
      edit: this.hasPermission(PrivilegesEnum.MARKETPLACE_SETTINGS_WRITE),
    };
  }

  get rolesPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.ROLES_READ),
      edit: this.hasPermission(PrivilegesEnum.ROLES_WRITE),
    };
  }

  get operatorsPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.OPERATORS_READ),
      edit: this.hasPermission(PrivilegesEnum.OPERATORS_WRITE),
    };
  }

  get operatorSettingsPermissions(): PermissionsUpdate {
    return {
      view: true,
      edit: true,
    };
  }

  get mediaPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.MEDIA_READ),
      edit: this.hasPermission(PrivilegesEnum.MEDIA_WRITE),
    };
  }

  get reviewsPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.REVIEWS_READ),
      edit: this.hasPermission(PrivilegesEnum.REVIEWS_WRITE),
    };
  }

  get reviewsSettingsPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.REVIEW_TEMPLATES_READ),
      edit: this.hasPermission(PrivilegesEnum.REVIEW_TEMPLATES_WRITE),
    };
  }

  get promoCodesPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.PROMOCODES_READ),
      edit: this.hasPermission(PrivilegesEnum.PROMOCODES_WRITE),
    };
  }

  get marketingCompaniesPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.MARKETING_COMPANIES_READ),
      edit: this.hasPermission(PrivilegesEnum.MARKETPLACE_SETTINGS_WRITE),
    };
  }

  get tagsPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.TAGS_READ),
      edit: this.hasPermission(PrivilegesEnum.TEMPLATE_WRITE),
    };
  }

  get negotiationsPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.NEGOTIATIONS_READ),
      edit: this.hasPermission(PrivilegesEnum.NEGOTIATIONS_WRITE),
    };
  }

  get partnersPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.PARTNER_PRODUCT_READ),
      edit: this.hasPermission(PrivilegesEnum.PARTNER_PRODUCT_WRITE),
    };
  }

  get regionsPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.USER_REGIONS_READ),
      edit: this.hasPermission(PrivilegesEnum.USER_REGIONS_WRITE),
    };
  }

  get branchesPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.BRANCHES_READ),
      edit: this.hasPermission(PrivilegesEnum.BRANCHES_WRITE),
    };
  }

  get disputesPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.DISPUTES_READ),
      edit: this.hasPermission(PrivilegesEnum.DISPUTES_WRITE),
    };
  }

  get categoriesPermissions(): PermissionsFullAccess {
    return {
      view: this.hasPermission(PrivilegesEnum.CATEGORY_READ),
      edit: this.hasPermission(PrivilegesEnum.CATEGORY_WRITE),
      delete: this.hasPermission(PrivilegesEnum.CATEGORY_WRITE),
      create: this.hasPermission(PrivilegesEnum.CATEGORY_WRITE),
    };
  }

  get categoriesFeePermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.CATEGORY_COMMISSION_READ),
      edit: this.hasPermission(PrivilegesEnum.CATEGORY_COMMISSION_WRITE),
    };
  }

  get invoicesPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.INVOICES_TEMPLATES_READ),
      edit: this.hasPermission(PrivilegesEnum.INVOICES_TEMPLATES_WRITE),
    };
  }

  get settlementPermissions(): PermissionsUpdate {
    return {
      view: this.builderInfoService.settings?.accessSettlement || false,
      edit: this.builderInfoService.settings?.accessSettlement || false,
    };
  }

  get tendersOffersPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.TENDER_OFFERS_READ),
      edit: this.hasPermission(PrivilegesEnum.TENDER_OFFERS_WRITE),
    };
  }

  get tendersPermissions(): PermissionsFullAccess {
    return {
      view: this.hasPermission(PrivilegesEnum.TENDERS_READ),
      edit: this.hasPermission(PrivilegesEnum.TENDERS_WRITE),
      delete: this.hasPermission(PrivilegesEnum.TENDERS_WRITE),
      create: this.hasPermission(PrivilegesEnum.TENDERS_WRITE),
    };
  }

  get tieredPricesPermissions(): PermissionsUpdate {
    return {
      view: !!this.builderInfoService.settings?.accessTieredPrices,
      edit: !!this.builderInfoService.settings?.accessTieredPrices,
    };
  }

  get sourcingPermissions(): PermissionsUpdate {
    return {
      view: this.futureSkusPermissions.view,
      edit: this.futureSkusPermissions.edit,
    };
  }

  get sourcingInputsPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.SOURCING_INPUTS_READ),
      edit: this.hasPermission(PrivilegesEnum.SOURCING_INPUTS_WRITE),
    };
  }

  get sourcingOutputsPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.SOURCING_OUTPUTS_READ),
      edit: this.hasPermission(PrivilegesEnum.SOURCING_OUTPUTS_WRITE),
    };
  }

  get fdsPricesPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.FDS_READ),
      edit: this.hasPermission(PrivilegesEnum.FDS_WRITE),
    };
  }

  get deliveryPeriodsPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.DELIVERY_PERIODS_READ),
      edit: this.hasPermission(PrivilegesEnum.DELIVERY_PERIODS_WRITE),
    };
  }

  get deliveryMethodsPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.MERCHANT_DELIVERY_METHODS_READ),
      edit: this.hasPermission(PrivilegesEnum.MERCHANT_DELIVERY_METHODS_WRITE),
    };
  }

  get paymentAttributesPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.PAYMENT_ATTRIBUTES_READ),
      edit: this.hasPermission(PrivilegesEnum.PAYMENT_ATTRIBUTES_WRITE),
    };
  }

  get licensesPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.LICENSES_READ),
      edit: this.hasPermission(PrivilegesEnum.LICENSES_WRITE),
    };
  }

  get seoPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.SEO_READ),
      edit: this.hasPermission(PrivilegesEnum.SEO_WRITE),
    };
  }

  get relatedItemsPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.RELATED_ITEMS_READ),
      edit: this.hasPermission(PrivilegesEnum.RELATED_ITEMS_WRITE),
    };
  }

  get flashSalesPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.PROMOTIONS_READ),
      edit: this.hasPermission(PrivilegesEnum.PROMOTIONS_WRITE),
    };
  }

  get refundsPermissions(): PermissionsUpdate {
    return {
      view: this.hasPermission(PrivilegesEnum.REFUNDS_READ),
      edit: this.hasPermission(PrivilegesEnum.REFUNDS_WRITE),
    };
  }

  hasPermission(flag: PrivilegesEnum) {
    return !!this.permissionsMap?.[flag];
  }

  protected getPermissionsMap(permissions: PrivilegesEnum[]): PermissionsMap {
    return permissions.reduce((acc, cur) => ((acc[cur] = cur), acc), {} as PermissionsMap);
  }
}
